import { inject, Injectable } from '@angular/core';
import { BufferAttribute, BufferGeometry, Mesh } from 'three';
import { MeshBVH } from 'three-mesh-bvh';
import { PLYLoader } from 'three/examples/jsm/loaders/PLYLoader.js';
import { EdgeSplitModifier } from 'three/examples/jsm/modifiers/EdgeSplitModifier.js';
import { mergeVertices } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { GeometryWorkerService, ProcessedGeometry } from './geometry-worker.service';
import { displacementRefineEdgeLength, refineLongEdges } from './refine-long-edges';

/**
 * Strip non-finite (NaN/Infinity) vertex data out of a result mesh.
 *
 * The runner's postprocessor occasionally emits a degenerate visualisation
 * PLY whose `position` attribute contains NaN values. Such geometry has a NaN
 * bounding sphere/box, and — critically — feeding it to `three-mesh-bvh`'s BVH
 * builder makes the centroid split heuristic degenerate, which manifests as the
 * editor "opening super long" (a near-hang while the BVH is built). This
 * neutralises the bad data at the point the geometry enters Three.js land:
 *  - non-finite position components are zeroed so bounds stay finite, and
 *  - for indexed geometry, any triangle that referenced a bad vertex is dropped
 *    so the collapsed-to-origin vertices don't draw stray slivers.
 *
 * Returns true if anything was changed (and logs a one-line warning), false for
 * already-clean geometry (the overwhelmingly common case — one linear scan).
 */
export function sanitizeNonFiniteGeometry(geometry: BufferGeometry): boolean {
  const position = geometry.getAttribute('position');
  if (!position) return false;

  const positions = position.array as Float32Array;
  const badVertices = new Set<number>();
  for (let i = 0; i < positions.length; i++) {
    if (!Number.isFinite(positions[i])) {
      positions[i] = 0;
      badVertices.add((i / 3) | 0);
    }
  }

  if (badVertices.size === 0) return false;

  const index = geometry.getIndex();
  if (index) {
    const indices = index.array as ArrayLike<number>;
    const kept: number[] = [];
    for (let t = 0; t + 2 < indices.length; t += 3) {
      const a = indices[t];
      const b = indices[t + 1];
      const c = indices[t + 2];
      if (!badVertices.has(a) && !badVertices.has(b) && !badVertices.has(c)) {
        kept.push(a, b, c);
      }
    }
    geometry.setIndex(kept);
  }

  position.needsUpdate = true;
  console.warn(
    `[GeometrySanitize] Dropped ${badVertices.size} vertices with non-finite positions from a result mesh — skipping degenerate geometry to avoid a load hang.`
  );
  return true;
}

@Injectable({
  providedIn: 'root'
})
export class GeometryProcessingService {
  private geometryWorker = inject(GeometryWorkerService);

  public organicMesh?: Mesh; // Cached organic PLY mesh
  public prismaticMesh?: Mesh; // Cached prismatic PLY mesh
  public loadCaseUrls: {
    organic: { id: string; x: string; y: string; z: string; t: string; s?: string }[];
    prismatic: { id: string; x: string; y: string; z: string; t: string; s?: string }[];
  } = { organic: [], prismatic: [] };

  /**
   * Convert ProcessedGeometry from worker to Three.js BufferGeometry
   */
  processedGeometryToBuffer(processed: ProcessedGeometry): BufferGeometry {
    const geometry = new BufferGeometry();
    geometry.setAttribute('position', new BufferAttribute(processed.positions, 3));
    geometry.setIndex(new BufferAttribute(processed.indices, 1));
    geometry.setAttribute('normal', new BufferAttribute(processed.normals, 3));
    geometry.computeBoundingSphere();
    geometry.computeBoundingBox();
    if (processed.serializedBVH) {
      // The worker built the BVH over these very arrays; its index is already
      // the geometry's index.
      geometry.boundsTree = MeshBVH.deserialize(processed.serializedBVH, geometry, { setIndex: false });
    }
    return geometry;
  }

  /**
   * Process PLY geometry - uses Web Worker if available, falls back to main thread.
   * `options.bvh` also builds a BVH for raycasting (in the worker, or on the
   * main thread on the fallback path). `options.refineForDisplacement` splits
   * long edges so the FEA displacement shader can bend coarse result meshes.
   */
  async processPLYGeometry(
    data: ArrayBuffer,
    cutOffAngle: number = 20,
    options: { bvh?: boolean; refineForDisplacement?: boolean } = {}
  ): Promise<BufferGeometry> {
    // Try worker first for better performance
    if (this.geometryWorker.isSupported) {
      try {
        const processed = await this.geometryWorker.processPLY(
          data,
          cutOffAngle,
          options.bvh === true,
          options.refineForDisplacement === true
        );
        return this.processedGeometryToBuffer(processed);
      } catch (e) {
        console.warn('Worker PLY processing failed, falling back to main thread:', e);
      }
    }

    // Fallback to main thread processing
    const loader = new PLYLoader();
    let geometry = loader.parse(data);
    // Strip non-finite vertices BEFORE the heavy EdgeSplit/mergeVertices passes —
    // a NaN-collapsed super-vertex makes them pathologically slow (see the worker
    // path's stripNonFiniteTriangles for the full explanation).
    sanitizeNonFiniteGeometry(geometry);
    geometry.computeVertexNormals();
    geometry.normalizeNormals();

    try {
      const edgeSplit = new EdgeSplitModifier();
      geometry = mergeVertices(edgeSplit.modify(geometry, (cutOffAngle * Math.PI) / 180, false));
    } catch (e) {
      // Keep original geometry on error
    }

    if (options.refineForDisplacement && geometry.index) {
      const positions = geometry.getAttribute('position').array as Float32Array;
      const normal = geometry.getAttribute('normal');
      if (normal) {
        const refined = refineLongEdges(
          positions,
          Uint32Array.from(geometry.index.array as ArrayLike<number>),
          normal.array as Float32Array,
          displacementRefineEdgeLength(positions)
        );
        if (refined.addedTriangles > 0) {
          geometry.setAttribute('position', new BufferAttribute(refined.positions, 3));
          geometry.setAttribute('normal', new BufferAttribute(refined.normals, 3));
          geometry.setIndex(new BufferAttribute(refined.indices, 1));
          geometry.computeBoundingBox();
          geometry.computeBoundingSphere();
        }
      }
    }

    if (options.bvh && geometry.index) geometry.boundsTree = new MeshBVH(geometry);
    return geometry;
  }

  // Helper to format bytes to human-readable format
  formatBytes(bytes: number): string {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  }
}
